import * as Sentry from '@sentry/react'
import { createClient } from 'graphql-ws'
import { v4 } from 'uuid'
import {
  ApolloClient,
  ApolloLink,
  fromPromise,
  HttpLink,
  NormalizedCacheObject,
  ServerParseError,
  split
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { ServerError } from '@apollo/client/link/utils/throwServerError'
import { getMainDefinition } from '@apollo/client/utilities'

import { logout, updateToken } from '@app/app/auth/actions/authActions'
import { authStore } from '@app/app/auth/store/authStore'
import { HTTPStatusCodeEnum } from '@app/model/common/HTTPStatusCodeEnum'
import { LogUtils } from '@app/utils/LogUtils'

import { apolloStore } from '../../modules/common/store/apolloStore'
import { Constants } from '../constants/Constants'
import { createApolloCache } from './cacheConfig'

const DEFAULT_TIMEOUT = 12_000

export const initApolloClient = (): void => {
  const apolloClient = getApolloClient()

  apolloStore.setApolloClient(apolloClient)
}

type ReturnType = ApolloClient<NormalizedCacheObject>

const getApolloClient = (): ReturnType => {
  const errorLink = onError(({ graphQLErrors, operation, forward }) => {
    const traceId = operation.getContext().traceId || undefined

    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (err.extensions) {
          err.extensions.traceId = traceId
        }

        switch (err.extensions?.code) {
          case 'UNAUTHENTICATED':
            return fromPromise(
              updateToken(3600).catch(() => {
                logout()
                return
              })
            )
              .filter((value) => Boolean(value))
              .flatMap(() => {
                const newAccessToken = authStore.useStore.getState().accessToken
                const oldHeaders = operation.getContext().headers

                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    authorization: `Bearer ${newAccessToken}`
                  }
                })
                return forward(operation)
              })
        }
      }
    }
  })

  const operationNameCounts: Record<string, number> = {}

  const retryLink = new RetryLink({
    attempts: {
      max: Constants.MAX_RETRIES,
      retryIf: async (error, operation) => {
        // Increment the count for the current operationName
        const operationName = operation.operationName

        if (operationNameCounts[operationName] >= Constants.MAX_RETRIES - 1) {
          logout()
        }

        try {
          if (error && hasStatusCode(error) && error.statusCode === HTTPStatusCodeEnum.UNAUTHORIZED) {
            if (operationName) {
              if (!operationNameCounts[operationName]) {
                operationNameCounts[operationName] = 0
              }
              operationNameCounts[operationName]++
            }

            await updateToken(3600)

            return true
          }

          return false
        } catch {
          return false
        }
      }
    },
    delay: { initial: 300, max: 1000 }
  })

  const fetchWithTimeout = (uri: URL | RequestInfo, options = {}, time: number): Promise<Response> => {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('Request timed out.'))
      }, time)
      fetch(uri, options).then(
        (response) => {
          clearTimeout(timer)
          resolve(response)
        },
        (err) => {
          clearTimeout(timer)
          reject(err)
        }
      )
    })
  }

  const customFetch: WindowOrWorkerGlobalScope['fetch'] = async (uri, options) => {
    if (!options) options = {}
    if (!options?.headers) {
      options.headers = {}
    }

    const authToken = authStore.useStore.getState().accessToken || ''

    const traceId = v4()
    options.headers = {
      ...options.headers,
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
      'trace-id': `${traceId}`
    }

    // Store traceId in options for later access in error handling
    ;(options as any).traceId = traceId

    Sentry.setContext('trace-id', { traceId })
    Sentry.setTag('trace-id', traceId)
    let updatedUri = uri

    // --- Sentry operationName capture for GraphQL ---
    // For any GraphQL call (uri contains '/graphql')
    if (typeof uri === 'string' && uri.includes('/graphql') && options && typeof options.body === 'string') {
      const parsedUri = new URL(uri)

      try {
        const { operationName, variables } = JSON.parse(options.body)

        if (operationName) {
          Sentry.setContext('graphql', { operationName })
        }

        if (parsedUri) {
          if (operationName) {
            parsedUri.searchParams.set('q', operationName)
          }

          for (const [key, value] of Object.entries(variables)) {
            if (key === 'id' || (key.endsWith('Id') && (typeof value === 'string' || typeof value === 'number'))) {
              parsedUri.searchParams.set(key, String(value))
            } else if (key === 'page' && typeof value === 'object' && value && 'from' in value && 'count' in value) {
              parsedUri.searchParams.set('pageOffset', String(value.from))
              parsedUri.searchParams.set('pageLimit', String(value.count))
            }
          }

          updatedUri = parsedUri.toString()
        }
      } catch {
        // ignore JSON parse errors
      }
    }
    // --- End Sentry operationName capture ---

    const timeoutFromHeader = options?.headers?.['x-timeout']
    const timeout = timeoutFromHeader || Number(DEFAULT_TIMEOUT)

    return fetchWithTimeout(updatedUri, options, timeout)
  }

  // Link to capture traceId and set it in operation context
  const traceIdLink = new ApolloLink((operation, forward) => {
    // Extract traceId from custom fetch options and set it in context
    const context = operation.getContext()
    const fetchOptions = context.fetchOptions || {}
    const traceId = fetchOptions.traceId || v4()

    operation.setContext({
      ...context,
      traceId,
      fetchOptions: {
        ...fetchOptions,
        traceId
      }
    })

    return forward(operation)
  })

  const httpLink = new HttpLink({
    uri: Constants.GRAPHQL_QUERY_URL,
    fetch: customFetch
  })

  let activeSocket, timedOut
  const websocketLink = new GraphQLWsLink(
    createClient({
      url: Constants.GRAPHQL_SUBSCRIPTIONS_URL,
      retryWait: async function randomizedExponentialBackoff(retries) {
        let retryDelay = 1000 // start with 1s delay

        for (let i = 0; i < retries; i++) {
          retryDelay *= 2
        }
        await new Promise((resolve) =>
          setTimeout(
            resolve,
            retryDelay +
              // add random timeout from 300ms to 3s
              Math.floor(Math.random() * (3000 - 300) + 300)
          )
        )
      },
      connectionParams: { authToken: authStore.useStore.getState().accessToken || '' },
      keepAlive: 10_000,
      retryAttempts: Infinity,
      on: {
        connected: (socket) => {
          console.info('websocket client TP connected!')
          activeSocket = socket
        },
        closed: () => {
          console.info('websocket client TP closed!')
        },
        error: (e) => {
          console.info('websocket client TP received an error!')
          LogUtils.logError(e, 'apolloActions', 'websocketLink', 'Websocket error')
        },
        ping: (received) => {
          if (!received)
            // sent
            timedOut = setTimeout(() => {
              if (activeSocket.readyState === WebSocket.OPEN) {
                activeSocket.close(4408, 'Request Timeout')
              }
            }, 5_000) // wait 5 seconds for the pong and then close the connection
        },
        pong: (received) => {
          if (received) {
            clearTimeout(timedOut) // pong is received, clear connection close timeout
          }
        }
      }
    })
  )

  // The split function takes three parameters:
  //
  // * A function that's called for each operation to execute
  // * The Link to use for an operation if the function returns a "truthy" value
  // * The Link to use for an operation if the function returns a "falsy" value

  // queries and mutations will use HTTP as normal, subscriptions will use WebSocket
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    websocketLink,
    ApolloLink.from([traceIdLink, retryLink, errorLink, httpLink])
  )

  return new ApolloClient({
    connectToDevTools: true,
    cache: createApolloCache(),
    link: splitLink,
    defaultOptions: {
      watchQuery: {
        nextFetchPolicy: 'cache-and-network'
      }
    }
  })
}

const hasStatusCode = (networkError: any): networkError is ServerError | ServerParseError => {
  return Boolean(networkError && networkError.statusCode)
}
